import 'get-root-node-polyfill/implement'; // for IE / Edge
import { h, render } from 'preact';
import mitt from 'mitt';
import { QueryClient } from 'react-query';

import './styles/index.css';
import App from './app';
import Tracer from './utils/tracer';
import Logger from './utils/logger';
import createRouter from './deprecated-router';
import initI18next, { setupTranslations } from './initializers/i18next';
import initMomentJs from './initializers/moment';
import configureApiClient, { createApiClient } from './initializers/api-client';
import getGlobalValue from './utils/get-global-value';
import createStore from './store';
import * as defaultSubscriptions from './subscriptions';
import {
  initializeError,
  initializeSuccess,
  notificationSMSDisplaySeverity,
} from './actions';
import { localStorage, sessionStorage } from './utils/namespaced-storage';
import createTrackMethod from './initializers/track-method';
import createErrorLogger from './initializers/error-logger';
import preLoadApp from './app-manifest/pre-loader';
import { values } from './utils/object';
import { NODE_ENV, SEVERITY } from './constants';
import { location, document } from './env/browser';

if (NODE_ENV === 'development') {
  // eslint-disable-next-line global-require
  require('preact/debug');
}

Tracer.init();

const apiClient = {};
const trackMethod = createTrackMethod(() => getGlobalValue('analytics'));
const errorLogger = createErrorLogger(getGlobalValue('Sentry'));
const router = createRouter();
const eventBus = mitt();
const queryClient = new QueryClient();
const deps = {
  apiClient,
  trackMethod,
  router,
  sessionStorage,
  localStorage,
  errorLogger,
  eventBus,
  queryClient,
};

const checkAndRedirectDouglasUrlToShore = () => {
  if (location.href.indexOf('douglas') > -1) {
    location.href = 'https://secure.shore.com';
  }
};

/* 
Since we no longer have contract with Douglas and 
we moved all the Douglas merchants to Shore contract,
So with this method we want to make sure if someone still uses
douglas url he should be redirected to Shore.
*/
checkAndRedirectDouglasUrlToShore();

const store = createStore(deps);

render(
  <App store={store} {...deps} />,
  document.body,
  document.getElementById('asMain'),
);

const smsDisplayNofification = (
  dispatch,
  {
    data: {
      // eslint-disable-next-line camelcase
      attributes: { current_usage },
    },
  },
) => {
  // eslint-disable-next-line camelcase
  switch (current_usage) {
    case '80%': {
      dispatch(notificationSMSDisplaySeverity(SEVERITY.WARNING));
      break;
    }
    case '100%': {
      dispatch(notificationSMSDisplaySeverity(SEVERITY.DANGER));
      break;
    }
    default: {
      dispatch(notificationSMSDisplaySeverity(SEVERITY.NONE));
    }
  }
};

(async function initialize() {
  try {
    Object.assign(
      apiClient,
      await configureApiClient(createApiClient, queryClient),
    );
    const {
      websocketListener,
      observeUnreadNotificationCount,
      ...subscriptions
    } = defaultSubscriptions;

    values(subscriptions).forEach(fn => fn(store, deps));

    const myAccountData = await apiClient.getMyAccountData();
    const currentMerchantData = await apiClient.getCurrentMerchantData();
    const whiteLabelConfig = await apiClient.getWhiteLabelConfig();

    observeUnreadNotificationCount(store, deps, currentMerchantData.id);
    websocketListener(apiClient)(myAccountData.id)(
      'private-channel.sms_monthly_allowance.merchant_accounts',
    )('sms_monthly_allowance.v1.limit_reached')(d => {
      smsDisplayNofification(store.dispatch, d);
    });

    // For Testing
    // const dummyData = { data: { attributes: { current_usage: '100%' } } };

    // setInterval(() => {
    //   smsDisplayNofification(store.dispatch, dummyData);
    // }, 15000);

    const { locale } = myAccountData;

    Tracer.setUser({
      merchantId: currentMerchantData.id,
      employeeId: myAccountData.employee.id,
    });

    Logger.init({
      merchantId: currentMerchantData.id,
      employeeId: myAccountData.employee.id,
    });

    await initMomentJs(locale);
    await initI18next(locale);
    await setupTranslations(locale);

    preLoadApp('search', locale);
    preLoadApp('launchpad', locale);

    store.dispatch(
      initializeSuccess(myAccountData, currentMerchantData, whiteLabelConfig),
    );
  } catch (error) {
    store.dispatch(initializeError(error));
  }
})();
